import React from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';
import { LAP, dialPosition, pointAt, sectorPath } from './clockModel';
import { tok, LABEL, weeks } from './clockUi';
import { isInZone } from '../depthReading';
import { SIDES } from './clockSide';

// The Cycle Clock's dial (DWLF-301): one lap from the last confirmed weekly low (or high, DWLF-552), the focused
// market's window drawn as sectors, each market a dot at its time-since-pivot and gap-spread radius.
const SIZE = 640;
const C = SIZE / 2;
const R_IN = 90;
const R_SPAN = 200;
const R_OUT = 300;

const TICKS = Array.from({ length: 24 }, (_, i) => {
  const p = (i / 24) * LAP;
  const [x1, y1] = pointAt(C, C, p, i % 6 === 0 ? 282 : 292);
  const [x2, y2] = pointAt(C, C, p, R_OUT);
  return { x1, y1, x2, y2 };
});
const zones = (side) => [
  { p: 1, text: `${side.pivot.toUpperCase()} WINDOW`, token: 'warn' },
  { p: 0.25, text: 'EARLY', token: 'ink-55' },
  { p: 0.625, text: 'MID-CYCLE', token: 'ink-55' },
  { p: 1.3, text: 'OVERDUE', token: 'down' },
];

function Seam({ side }) {
  const [x1, y1] = pointAt(C, C, 0, R_IN);
  const [x2, y2] = pointAt(C, C, 0, R_OUT);
  const [lx, ly] = pointAt(C, C, 0.03, R_OUT + 12);
  return (
    <g>
      <line x1={x1} y1={y1} x2={x2} y2={y2} style={{ stroke: tok('accent-40') }} strokeDasharray="3 3" />
      <text x={lx} y={ly} textAnchor="middle" fontSize="10" letterSpacing="1.5" style={{ ...LABEL, fill: tok('accent') }}>LAST {side.pivot.toUpperCase()}</text>
    </g>
  );
}

function FocusSpoke({ focus }) {
  const pos = focus?.onDial ? dialPosition(focus) : null;
  if (pos == null) return null;
  const [x, y] = pointAt(C, C, pos, R_IN + focus.radius * R_SPAN);
  const [ix, iy] = pointAt(C, C, pos, R_IN);
  return <line x1={ix} y1={iy} x2={x} y2={y} style={{ stroke: tok('ink-30') }} strokeDasharray="2 3" />;
}

// `size`: the drawn size (any CSS length), from the page that knows how much room it has; SIZE when absent.
// `side`: which pivot the dial times (clockSide's SIDES entry), lows when absent.
export default function ClockDial({ size = `${SIZE}px`, side = SIDES.low, onDial, focus, inFilter, loading, onSelect, onHover }) {
  return (
    <Box sx={{ position: 'relative', width: { xs: '100%', lg: size }, maxWidth: size, aspectRatio: '1 / 1', mx: 'auto' }}>
      <svg viewBox={`0 0 ${SIZE} ${SIZE}`} width="100%" height="100%" style={{ position: 'absolute', inset: 0 }} role="img" aria-label="Cycle clock dial">
        <circle cx={C} cy={C} r={R_OUT} style={{ fill: tok('sunk'), stroke: tok('ink-10') }} />
        {focus?.onDial && focus.arcs.earliest != null && focus.arcs.hardMax != null && (
          <path d={sectorPath(C, C, focus.arcs.earliest, Math.min(focus.arcs.hardMax, LAP - 0.01), R_IN, R_OUT)} style={{ fill: tok('warn-08') }} />
        )}
        {focus?.onDial && focus.arcs.opens != null && focus.arcs.closes != null && focus.arcs.opens < LAP && (
          <path d={sectorPath(C, C, focus.arcs.opens, Math.min(focus.arcs.closes, LAP - 0.01), R_IN, R_OUT)} style={{ fill: tok('warn-15'), stroke: tok('warn-40') }} />
        )}
        <circle cx={C} cy={C} r={190} style={{ fill: 'none', stroke: tok('ink-06') }} strokeDasharray="2 6" />
        <circle cx={C} cy={C} r={R_IN} style={{ fill: tok('bg'), stroke: tok('ink-10') }} />
        {TICKS.map((t, i) => <line key={i} {...t} style={{ stroke: tok('ink-20') }} />)}
        <line x1={C} y1={20} x2={C} y2={SIZE - 20} style={{ stroke: tok('ink-05') }} />
        <line x1={20} y1={C} x2={SIZE - 20} y2={C} style={{ stroke: tok('ink-05') }} />
        {zones(side).map((l) => {
          const [x, y] = pointAt(C, C, l.p, R_OUT - 24);
          return <text key={l.text} x={x} y={y + 4} textAnchor="middle" fontSize="11" letterSpacing="2" style={{ ...LABEL, fill: tok(l.token) }}>{l.text}</text>;
        })}
        <Seam side={side} />
        <text x={C} y={C - 8} textAnchor="middle" fontSize="10" letterSpacing="1.5" style={{ ...LABEL, fill: tok('ink-50') }}>TODAY</text>
        <text x={C} y={C + 18} textAnchor="middle" fontSize="22" fontWeight="600" style={{ fontFamily: '"Space Grotesk", sans-serif', fill: tok('text') }}>
          {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
        </text>
        <FocusSpoke focus={focus} />
      </svg>

      {onDial.map((m) => {
        const pos = dialPosition(m);
        const [x, y] = pointAt(C, C, pos, R_IN + m.radius * R_SPAN);
        const isFocus = focus?.symbol === m.symbol;
        const visible = inFilter(m);
        // DWLF-416: a market whose last close is IN ZONE (its typical fall from the high, or rise from the low) gets a ring.
        const inZone = isInZone(m.depth);
        return (
          <ButtonBase
            key={m.symbol}
            // A dot the stage/depth filters exclude is a faded mark only: out of the tab order, hidden from
            // assistive tech and not clickable, since the clock never focuses an excluded market (DWLF-511).
            disabled={!visible}
            tabIndex={visible ? 0 : -1}
            aria-hidden={visible ? undefined : true}
            onClick={() => onSelect(m.symbol)}
            onMouseEnter={() => onHover(m.symbol)}
            onMouseLeave={() => onHover(null)}
            onFocus={() => onHover(m.symbol)}
            onBlur={() => onHover(null)}
            aria-label={`${m.symbol}, ${m.stage.label}, week ${Math.round(m.weeksSincePivot)} of about ${weeks(m.typicalWeeks)}${inZone ? ', depth IN ZONE' : ''}`}
            sx={{
              position: 'absolute', left: `${(x / SIZE) * 100}%`, top: `${(y / SIZE) * 100}%`,
              transform: `translate(-9px, -9px) scale(${visible ? (isFocus ? 1.25 : 1) : 0.7})`,
              opacity: visible ? 1 : 0.15, zIndex: isFocus ? 5 : visible ? 2 : 1, pointerEvents: visible ? 'auto' : 'none',
              display: 'flex', alignItems: 'center', gap: 0.75, transition: 'transform .3s, opacity .3s',
            }}
          >
            <Box component="span" sx={{ width: 18, height: 18, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Box component="span" sx={{ width: isFocus ? 12 : 9, height: isFocus ? 12 : 9, backgroundColor: tok(m.stage.token), outline: `2px solid ${tok('bg')}`, boxShadow: inZone ? `0 0 0 4px ${tok('accent')}` : 'none' }} />
            </Box>
            {/* Label only what needs attention, plus the focused market: quiet mid-cycle dots stay
                unlabelled so recent pivots don't pile up into an unreadable cluster. */}
            {(isFocus || inZone || m.stage.attention) && (
              <Box component="span" sx={{ ...LABEL, letterSpacing: '0.06em', fontSize: '0.6875rem', px: 0.6, py: 0.25, backgroundColor: tok('bg-85'), color: isFocus ? tok('accent') : tok('ink-80') }}>
                {m.symbol}
              </Box>
            )}
          </ButtonBase>
        );
      })}
      {loading && (
        <Typography sx={{ position: 'absolute', left: 0, right: 0, top: '58%', textAlign: 'center', fontSize: '0.75rem', color: tok('ink-50') }}>Loading windows…</Typography>
      )}
    </Box>
  );
}
